import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';

function SelfTaskForm() {
  const [taskName, setTaskName] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState('normal');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [tasks, setTasks] = useState([]);

  const loadTasks = useCallback(async () => {
    const { data } = await supabase.from('tanner_tasks').select('*').order('created_at', { ascending: false });
    setTasks(data || []);
  }, []);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!taskName.trim()) return;
    setSubmitting(true);
    await supabase.from('tanner_tasks').insert({
      task_name: taskName.trim(),
      due_date: dueDate || null,
      notes: notes.trim() || null,
      priority,
      status: 'todo',
      completed: false,
    });
    setSubmitted(true); setSubmitting(false); loadTasks();
  };

  const pending = tasks.filter(t => !t.completed);

  const priorities = [
    { id: 'normal', label: '😊 Normal', color: '#64748b' },
    { id: 'medium', label: '🟡 Medium', color: '#f59e0b' },
    { id: 'high', label: '🔴 High', color: '#ef4444' },
  ];

  const inputStyle = {
    width: '100%',
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    padding: '12px 14px',
    color: '#0f172a',
    fontSize: 15,
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc, #e2e8f0)', padding: 20, fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        input[type="date"] { color-scheme: light; }
        input, button, textarea { font-family: inherit; }
        .self-input:focus { border-color: #3b82f6 !important; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
      `}</style>
      <div style={{ maxWidth: 500, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 28, paddingTop: 20 }}>
          <div style={{ width: 70, height: 70, borderRadius: 18, background: 'linear-gradient(135deg, #3b82f6, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 32, boxShadow: '0 8px 24px rgba(59,130,246,0.3)' }}>⚡</div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.5px' }}>Quick Add</h1>
          <p style={{ color: '#64748b', margin: '8px 0 0', fontSize: 14 }}>Add a task to My Tasks</p>
        </div>

        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 20, padding: 24, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          {submitted ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <div style={{ width: 70, height: 70, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 36 }}>✓</div>
              <h2 style={{ color: '#10b981', margin: '0 0 8px', fontSize: 20, fontWeight: 700 }}>Added!</h2>
              <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 20px' }}>Task saved to your list.</p>
              <button onClick={() => { setTaskName(''); setDueDate(''); setNotes(''); setPriority('normal'); setSubmitted(false); }} style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: 'white', border: 'none', borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(59,130,246,0.3)' }}>Add Another</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Task Name *</label>
                <input value={taskName} onChange={e => setTaskName(e.target.value)} placeholder="What needs to be done?" style={inputStyle} className="self-input" />
              </div>
              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Priority</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {priorities.map(p => (
                    <button key={p.id} type="button" onClick={() => setPriority(p.id)} style={{ flex: 1, padding: '10px 8px', border: priority === p.id ? `2px solid ${p.color}` : '2px solid #e2e8f0', background: priority === p.id ? `${p.color}12` : 'white', color: priority === p.id ? p.color : '#64748b', fontSize: 12, fontWeight: 600, cursor: 'pointer', borderRadius: 8 }}>{p.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Due Date</label>
                <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={inputStyle} className="self-input" />
              </div>
              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes..." rows={2} style={{ ...inputStyle, resize: 'vertical' }} className="self-input" />
              </div>
              <button type="submit" disabled={submitting} style={{ background: submitting ? '#93c5fd' : 'linear-gradient(135deg, #3b82f6, #2563eb)', color: 'white', border: 'none', borderRadius: 12, padding: 15, fontSize: 15, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', boxShadow: submitting ? 'none' : '0 4px 12px rgba(59,130,246,0.3)', marginTop: 4 }}>
                {submitting ? 'Adding...' : '⚡ Add Task'}
              </button>
            </form>
          )}

          {pending.length > 0 && (
            <div style={{ marginTop: 20, borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
              <p style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pending ({pending.length})</p>
              {pending.slice(0, 5).map(t => (
                <div key={t.id} style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 12px', marginBottom: 6, color: '#0f172a', fontSize: 13, fontWeight: 500, border: '1px solid #e2e8f0', borderLeft: t.priority === 'high' ? '3px solid #ef4444' : t.priority === 'medium' ? '3px solid #f59e0b' : '3px solid #e2e8f0' }}>
                  {t.task_name}
                  {t.due_date && <span style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8' }}>· {new Date(t.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                </div>
              ))}
              {pending.length > 5 && <p style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', margin: '8px 0 0' }}>+{pending.length - 5} more</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SelfTaskForm;
